import React, { useState, useEffect, useRef } from 'react';
import TopNav from './TopNav';
import MobileSettings from './MobileSettings';
import MobileFeed from './MobileFeed';
import MobileInputBar from './MobileInputBar';
import MobileProfile from './MobileProfile';
import MobilePeopleList from './MobilePeopleList';
import MobileGroupsList from './MobileGroupsList';
import MobileGroupDetail from './MobileGroupDetail';
import MobileEditGroup from './MobileEditGroup';
import MobileSearch from './MobileSearch';
import MobileAddPersonModal from './MobileAddPersonModal';
import { useChatLogic } from '../../hooks/useChatLogic';
import { authPost, authFetch } from '../../lib/authFetch';
import { supabase } from '../../lib/supabase';
import './MobileApp.css';

function MobileApp({ donors, groups, savedLists = [], savedTables = [], onRefreshDonors, user, subscription, onSignOut }) {
  // View state: 0 = Feed, 1 = People, 2 = Groups
  const [viewIndex, setViewIndex] = useState(0);
  const [showGroupDetail, setShowGroupDetail] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [localGroups, setLocalGroups] = useState(groups || []);
  const [localLists, setLocalLists] = useState(savedLists || []);
  const [activeList, setActiveList] = useState(null);
  const [localTables, setLocalTables] = useState(savedTables || []);
  const [activeTable, setActiveTable] = useState(null);
  const [highlightedPerson, setHighlightedPerson] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);

  // User context state (for AI personalization)
  const [userContext, setUserContext] = useState(null);

  // Save as list state
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [pendingListEntry, setPendingListEntry] = useState(null);
  const [savingList, setSavingList] = useState(false);

  // Create group modal state (lifted from MobileGroupsList to render outside SwipeContainer)
  const [showCreateGroupSheet, setShowCreateGroupSheet] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupContext, setNewGroupContext] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [pendingPersonForGroup, setPendingPersonForGroup] = useState(null); // Person to add after group creation

  // Add person modal state
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [addPersonPrefill, setAddPersonPrefill] = useState({ name: '', note: '', groupId: null });

  // Input bar compact state when scrolling
  const [inputCompact, setInputCompact] = useState(false);

  // Nav hidden state (only for Feed view)
  const [navHidden, setNavHidden] = useState(false);

  const feedEntryRefs = useRef({});

  // Navigation callbacks for chat logic hook
  const handleNavigateToGroup = (group) => {
    setViewIndex(2);
    setSelectedGroup(group);
    setShowGroupDetail(true);
  };

  const handleNavigateToPerson = (personName) => {
    // If navigating to a different person while profile is open,
    // briefly clear selection to force re-render
    if (selectedPerson && selectedPerson.name !== personName) {
      setSelectedPerson(null);
      // Use setTimeout to allow state to update before setting new person
      setTimeout(() => {
        const donorData = donors?.[personName];
        setSelectedPerson({
          name: personName,
          ...(donorData || {})
        });
      }, 50);
    } else {
      const donorData = donors?.[personName];
      setSelectedPerson({
        name: personName,
        ...(donorData || {})
      });
    }
  };

  const handleNavigateToList = (listPreview) => {
    // Find the group
    const group = localGroups.find(g => g.id === listPreview.groupId);
    if (group) {
      // Navigate to Groups view and show the list
      setViewIndex(2);
      setSelectedGroup(group);
      setShowGroupDetail(true);
      setActiveList({
        id: listPreview.listId,
        name: listPreview.listName,
        organizationPrompt: listPreview.listPrompt,
        groups: listPreview.groups,
        group_id: listPreview.groupId
      });
    } else {
      // Fallback - just go to groups view
      setViewIndex(2);
    }
  };

  const handleShowCreateGroupSheet = (forPerson, suggestedName) => {
    setPendingPersonForGroup(forPerson || null);
    setNewGroupName(suggestedName || '');
    setShowCreateGroupSheet(true);
  };

  // Handler to open add person modal with prefilled data (called from chat when user mentions someone new)
  const handleOpenAddPersonModal = (name, note, groupId) => {
    setAddPersonPrefill({ name: name || '', note: note || '', groupId: groupId || null });
    setShowAddPersonModal(true);
  };

  // Load/refresh user context (defined before useChatLogic so it can be passed as callback)
  const refreshUserContext = async () => {
    try {
      const response = await authFetch('/api/user-context');
      if (response.ok) {
        const data = await response.json();
        setUserContext(data.userContext);
      }
    } catch (error) {
      console.error('Failed to load user context:', error);
    }
  };

  // Save user context (for settings)
  const handleSaveUserContext = async (context) => {
    try {
      const response = await authFetch('/api/user-context', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userContext: context, onboardingComplete: true })
      });
      if (response.ok) {
        const data = await response.json();
        setUserContext(data.userContext);
      }
    } catch (error) {
      console.error('Failed to save user context:', error);
      throw error;
    }
  };

  // Use the shared chat logic hook
  const {
    feedEntries,
    feedLoading,
    isSubmitting,
    streamingText,
    streamingEntryId,
    narrativeUpdate,
    handleSubmit,
    handleAddNoteFromProfile,
    handleGroupSelect,
    handleSkipGroupSelect,
    handleCreateGroupFromFeed,
    handleViewList,
    handleCreateListForGroup,
    handleConfirmAdd,
    handleDeclineAdd,
    handleResearchPerson,
    handleSkipResearch,
    handleSkipMoreInfo,
    handleAcceptResearch,
    handleDeclineResearch,
    handleResearchOptionSelect
  } = useChatLogic({
    donors,
    groups: localGroups,
    savedLists: localLists,
    savedTables: localTables,
    onRefreshDonors,
    onNavigateToGroup: handleNavigateToGroup,
    onNavigateToPerson: handleNavigateToPerson,
    onNavigateToList: handleNavigateToList,
    onShowCreateGroupSheet: handleShowCreateGroupSheet,
    onOpenAddPersonModal: handleOpenAddPersonModal,
    setLocalGroups,
    setLocalLists,
    setLocalTables,
    userContext, // Pass cached user context to avoid DB fetch on every chat
    onRefreshUserContext: refreshUserContext // Refresh after onboarding
  });

  // Update selectedPerson when donors data changes
  useEffect(() => {
    if (selectedPerson && donors?.[selectedPerson.name]) {
      const updatedDonor = donors[selectedPerson.name];
      if (JSON.stringify(updatedDonor) !== JSON.stringify(selectedPerson)) {
        setSelectedPerson({
          name: selectedPerson.name,
          ...updatedDonor
        });
      }
    }
  }, [donors, selectedPerson?.name]);

  const handleSubmitNote = handleSubmit;

  const handleNameTap = (donorName) => {
    const donorData = donors?.[donorName];
    // Open profile even if donor isn't in local state yet (e.g., just added)
    setSelectedPerson({
      name: donorName,
      ...(donorData || {})
    });
  };

  const handleCloseProfile = () => {
    setSelectedPerson(null);
  };

  const handleSelectPerson = (donor) => {
    setSelectedPerson({
      name: donor.name,
      ...donor
    });
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setShowGroupDetail(true);
  };

  const handleBackFromGroupDetail = () => {
    setShowGroupDetail(false);
    setSelectedGroup(null);
    setActiveList(null);
    setActiveTable(null);
  };

  // List handlers
  const handleSelectList = (list) => {
    setActiveList(list);
  };

  const handleBackFromList = () => {
    setActiveList(null);
  };

  // Table handlers
  const handleSelectTable = (table) => {
    setActiveTable(table);
  };

  const handleBackFromTable = () => {
    setActiveTable(null);
  };

  // Create a new list for a group
  const handleCreateList = async (prompt, group) => {
    try {
      // Get the members of this group to organize
      const groupMembers = Object.entries(donors || {})
        .filter(([, data]) => data.groupId === group.id || data.group_id === group.id)
        .map(([name]) => name);

      if (groupMembers.length === 0) {
        console.error('No members in group to organize');
        return null;
      }

      // Call the organize-list API
      const response = await authPost('/api/organize-list', {
        query: prompt,
        mode: 'real',
        groupId: group.id,
        people: groupMembers
      });

      if (!response.ok) throw new Error('Failed to organize list');

      const data = await response.json();

      // Save the list
      const saveResponse = await authPost('/api/lists/save', {
        organizationPrompt: prompt,
        groups: data.groups,
        mode: 'real',
        groupId: group.id
      });

      if (!saveResponse.ok) throw new Error('Failed to save list');

      const saveData = await saveResponse.json();

      // Add the new list to local state (ensure groupId is set)
      if (saveData.list) {
        const listWithGroupId = {
          ...saveData.list,
          groupId: saveData.list.groupId || saveData.list.group_id || group.id
        };
        setLocalLists(prev => [...prev, listWithGroupId]);
        return listWithGroupId;
      }

      return null;
    } catch (error) {
      console.error('Failed to create list:', error);
      throw error;
    }
  };

  // Save as list from feed - open group picker
  const handleSaveAsList = (entry) => {
    setPendingListEntry(entry);
    setShowGroupPicker(true);
  };

  // Create list from feed response for a selected group
  const handleSaveListToGroup = async (group) => {
    if (!pendingListEntry || savingList) return;
    setSavingList(true);

    try {
      // Find the question that generated this answer
      const questionEntry = feedEntries.find(e =>
        e.type === 'question' &&
        new Date(e.timestamp) < new Date(pendingListEntry.timestamp)
      );
      const prompt = questionEntry?.message || 'Organized list';

      // Get the members of this group to organize
      const groupMembers = Object.entries(donors || {})
        .filter(([, data]) => data.groupId === group.id || data.group_id === group.id)
        .map(([name]) => name);

      if (groupMembers.length === 0) {
        console.error('No members in group to organize');
        setShowGroupPicker(false);
        setPendingListEntry(null);
        setSavingList(false);
        return;
      }

      // Call the organize-list API
      const response = await authPost('/api/organize-list', {
        query: prompt,
        mode: 'real',
        groupId: group.id,
        people: groupMembers
      });

      if (!response.ok) throw new Error('Failed to organize list');

      const data = await response.json();

      // Save the list
      const saveResponse = await authPost('/api/lists/save', {
        organizationPrompt: prompt,
        groups: data.groups,
        mode: 'real',
        groupId: group.id
      });

      if (!saveResponse.ok) throw new Error('Failed to save list');

      const saveData = await saveResponse.json();

      // Add the new list to local state (ensure groupId is set)
      let newList = null;
      if (saveData.list) {
        newList = {
          ...saveData.list,
          groupId: saveData.list.groupId || saveData.list.group_id || group.id
        };
        setLocalLists(prev => [...prev, newList]);
      }

      // Close picker and go to the group
      setShowGroupPicker(false);
      setPendingListEntry(null);
      setSelectedGroup(group);
      setShowGroupDetail(true);
      if (newList) {
        setActiveList(newList);
      }
    } catch (error) {
      console.error('Failed to save list:', error);
    } finally {
      setSavingList(false);
    }
  };

  // Create a new table for a group
  const handleCreateTable = async (prompt, group, members) => {
    try {
      if (!members || members.length === 0) {
        console.error('No members in group to create table from');
        return null;
      }

      // Call the existing generate-table API endpoint
      const response = await authPost('/api/generate-table', {
        prompt,
        groupId: group.id,
        mode: 'real'
      });

      if (!response.ok) throw new Error('Failed to create table');

      const data = await response.json();

      // Now save the table to persistent storage
      const saveResponse = await authPost('/api/tables/save', {
        name: prompt,
        columns: data.columns || ['Name', 'Amount'],
        rows: data.rows || members.map(m => ({ Name: m.name, Amount: m.amount })),
        groupId: group.id,
        mode: 'real'
      });

      if (!saveResponse.ok) throw new Error('Failed to save table');

      const saveData = await saveResponse.json();
      const newTable = saveData.table;

      setLocalTables(prev => [newTable, ...prev]);
      return newTable;
    } catch (error) {
      console.error('Failed to create table:', error);
      // Create a basic table with just names and amounts as fallback
      const fallbackTable = {
        id: `table-${Date.now()}`,
        name: prompt,
        groupId: group.id,
        columns: ['Name', 'Amount'],
        rows: members.map(m => ({
          Name: m.name,
          Amount: m.amount || '—'
        })),
        createdAt: new Date().toISOString()
      };
      setLocalTables(prev => [fallbackTable, ...prev]);
      return fallbackTable;
    }
  };

  // Add a column to an existing table
  const handleAddTableColumn = async (columnPrompt, table) => {
    try {
      // Get the current table members (list of names)
      const people = (table.rows || []).map(row => row.name || row.Name);

      // Call the existing populate-column API endpoint
      const response = await authPost('/api/populate-column', {
        columnName: columnPrompt,
        people,
        groupId: table.groupId,
        mode: 'real'
      });

      if (!response.ok) throw new Error('Failed to add column');

      const data = await response.json();
      const values = data.values || {};

      // Update the table with the new column
      const updatedTable = {
        ...table,
        columns: [...(table.columns || []), columnPrompt],
        rows: table.rows.map(row => {
          const name = row.name || row.Name;
          return {
            ...row,
            [columnPrompt]: values[name] || '—'
          };
        })
      };

      // Save the updated table to the API
      await authPost('/api/tables/update', {
        tableId: table.id,
        columns: updatedTable.columns,
        rows: updatedTable.rows,
        mode: 'real'
      });

      setLocalTables(prev => prev.map(t => t.id === table.id ? updatedTable : t));
      setActiveTable(updatedTable);

      return updatedTable;
    } catch (error) {
      console.error('Failed to add column:', error);
      // Just add the column with placeholder values
      const updatedTable = {
        ...table,
        columns: [...(table.columns || []), columnPrompt],
        rows: table.rows.map(row => ({
          ...row,
          [columnPrompt]: '—'
        }))
      };
      setLocalTables(prev => prev.map(t => t.id === table.id ? updatedTable : t));
      setActiveTable(updatedTable);
      return updatedTable;
    }
  };

  // Sync local groups with prop
  useEffect(() => {
    if (groups) {
      setLocalGroups(groups);
    }
  }, [groups]);

  // Sync local lists with prop
  useEffect(() => {
    if (savedLists) {
      setLocalLists(savedLists);
    }
  }, [savedLists]);

  // Sync local tables with prop
  useEffect(() => {
    if (savedTables && savedTables.length > 0) {
      setLocalTables(savedTables);
    }
  }, [savedTables]);

  // Load tables from API on mount
  useEffect(() => {
    const loadTables = async () => {
      try {
        const response = await authFetch('/api/tables?mode=real');
        if (response.ok) {
          const data = await response.json();
          if (data.tables && data.tables.length > 0) {
            setLocalTables(data.tables);
          }
        }
      } catch (error) {
        console.error('Failed to load tables:', error);
      }
    };
    loadTables();
  }, []);

  // Load user context on mount (for AI personalization)
  useEffect(() => {
    refreshUserContext();
  }, []);

  // Create a new group
  const handleCreateGroup = async (name, context = null) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('groups')
        .insert([{
          user_id: session.user.id,
          name: name.trim(),
          color: null,
          context: context?.trim() || null
        }])
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      const newGroup = { ...data };
      setLocalGroups(prev => [...prev, newGroup]);

      return newGroup;
    } catch (err) {
      console.error('Failed to create group:', err);
      throw err;
    }
  };

  // Handle opening the create group sheet (called from MobileGroupsList)
  const handleOpenCreateGroupSheet = () => {
    setShowCreateGroupSheet(true);
  };

  // Handle submitting the create group sheet
  const handleSubmitCreateGroupSheet = async () => {
    if (!newGroupName.trim() || isCreatingGroup) return;

    setIsCreatingGroup(true);
    try {
      const newGroup = await handleCreateGroup(newGroupName.trim(), newGroupContext);
      const personToAdd = pendingPersonForGroup;
      setNewGroupName('');
      setNewGroupContext('');
      setPendingPersonForGroup(null);
      setShowCreateGroupSheet(false);

      if (newGroup) {
        // If there's a pending person, add them to the new group
        if (personToAdd) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              // Check if person already exists
              const { data: existing } = await supabase
                .from('donors')
                .select('id')
                .eq('user_id', session.user.id)
                .eq('name', personToAdd)
                .limit(1);

              if (existing && existing.length > 0) {
                // Update existing donor with new group
                await supabase
                  .from('donors')
                  .update({ group_id: newGroup.id })
                  .eq('id', existing[0].id);
              } else {
                // Create new donor in the group
                await supabase
                  .from('donors')
                  .insert([{
                    user_id: session.user.id,
                    name: personToAdd,
                    amount: 'Unknown',
                    narrative: '',
                    last_contact: new Date().toISOString().split('T')[0],
                    group_id: newGroup.id
                  }]);
              }
              // Refresh donors then navigate to person's profile
              if (onRefreshDonors) onRefreshDonors();
              // Navigate to the person's profile
              setTimeout(() => {
                setSelectedPerson({ name: personToAdd, group_id: newGroup.id });
              }, 300);
            }
          } catch (addError) {
            console.error('Error adding person to group:', addError);
          }
        } else {
          // No pending person, just go to the group
          handleSelectGroup(newGroup);
        }
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Update a group
  const handleUpdateGroup = async (groupId, updates) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('groups')
        .update(updates)
        .eq('id', groupId)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setLocalGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...data } : g));

      // Update selected group if it's the one being edited
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(prev => ({ ...prev, ...data }));
      }

      return data;
    } catch (err) {
      console.error('Failed to update group:', err);
      throw err;
    }
  };

  // Refresh all lists and tables for a group when members change
  const refreshGroupListsAndTables = async (groupId) => {
    if (!groupId) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Find lists for this group
    const groupLists = localLists.filter(l =>
      l.groupId === groupId || l.group_id === groupId
    );

    // Find tables for this group
    const groupTables = localTables.filter(t =>
      t.groupId === groupId || t.group_id === groupId
    );

    // Refresh each list
    for (const list of groupLists) {
      try {
        const response = await fetch('/api/lists/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            listId: list.id,
            mode: 'real',
            organizationPrompt: list.organizationPrompt || list.organization_prompt,
            listName: list.name,
            currentGroups: list.groups,
            groupId: groupId,
            forceRegenerate: false  // Don't force full regeneration - just add new members
          })
        });

        if (response.ok) {
          const data = await response.json();
          const newGroups = data.groups || data.list?.groups;
          if (newGroups) {
            // Update local state with refreshed list
            setLocalLists(prev => prev.map(l =>
              l.id === list.id ? { ...l, groups: newGroups } : l
            ));
            // Update active list if it's the one being refreshed
            if (activeList?.id === list.id) {
              setActiveList(prev => ({ ...prev, groups: newGroups }));
            }
          }
        }
      } catch (err) {
        console.error('Error refreshing list:', list.id, err);
      }
    }

    // Refresh each table
    for (const table of groupTables) {
      try {
        const response = await fetch('/api/tables/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            tableId: table.id,
            mode: 'real'
          })
        });

        if (response.ok) {
          const data = await response.json();
          const updatedTable = data.table;
          if (updatedTable) {
            // Update local state
            setLocalTables(prev => prev.map(t =>
              t.id === table.id ? { ...t, ...updatedTable } : t
            ));
            // Update active table if it's the one being refreshed
            if (activeTable?.id === table.id) {
              setActiveTable(prev => ({ ...prev, ...updatedTable }));
            }
          }
        }
      } catch (err) {
        console.error('Error refreshing table:', table.id, err);
      }
    }
  };

  // Add person to group
  const handleAddPersonToGroup = async (person, group) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Update the donor's group_id
      const { error } = await supabase
        .from('donors')
        .update({ group_id: group.id })
        .eq('id', person.id);

      if (error) throw error;

      // Refresh donors to reflect the change
      if (onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 300);
      }

      // Refresh lists and tables for this group
      setTimeout(() => refreshGroupListsAndTables(group.id), 500);

      return true;
    } catch (err) {
      console.error('Failed to add person to group:', err);
      throw err;
    }
  };

  // Create a new person and add them to a group
  const handleCreatePerson = async (name, group) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch('/api/donors/add-with-group', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          groupId: group.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create person');
      }

      const result = await response.json();

      // Refresh donors to reflect the change
      if (onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 300);
      }

      // Refresh lists and tables for this group
      setTimeout(() => refreshGroupListsAndTables(group.id), 500);

      // Return the created/updated donor data in the format the profile expects
      return {
        id: result.donor.id,
        name: result.donor.name,
        amount: result.donor.amount,
        narrative: result.donor.narrative,
        lastContact: result.donor.last_contact,
        nextMeeting: result.donor.next_meeting,
        nextTask: result.donor.next_task,
        groupId: result.donor.group_id,
        group_id: result.donor.group_id
      };
    } catch (err) {
      console.error('Failed to create person:', err);
      throw err;
    }
  };

  // Handle add person modal submit (with research + note merging)
  const handleAddPersonSubmit = async (name, info, groupId, personalNote) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Process narrative with AI - use different endpoints based on what we have
      let finalNarrative = '';
      const hasResearch = info && info.trim();
      const hasNote = personalNote && personalNote.trim();

      if (hasResearch && hasNote) {
        // We have BOTH research and personal note - use merge-narrative to combine them properly
        try {
          const mergeResponse = await authFetch('/api/merge-narrative', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              research: info.trim(),
              personalNote: personalNote.trim(),
              personName: name
            })
          });
          if (mergeResponse.ok) {
            const data = await mergeResponse.json();
            finalNarrative = data.narrative || `${info.trim()}\n\n${personalNote.trim()}`;
          } else {
            finalNarrative = `${info.trim()}\n\n${personalNote.trim()}`;
          }
        } catch (err) {
          console.error('Error merging narrative:', err);
          finalNarrative = `${info.trim()}\n\n${personalNote.trim()}`;
        }
      } else if (hasNote) {
        // Only personal note - enhance it
        try {
          const enhanceResponse = await authFetch('/api/enhance-note', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              note: personalNote.trim(),
              personName: name
            })
          });
          if (enhanceResponse.ok) {
            const data = await enhanceResponse.json();
            finalNarrative = data.narrative || personalNote.trim();
          } else {
            finalNarrative = personalNote.trim();
          }
        } catch (err) {
          console.error('Error enhancing note:', err);
          finalNarrative = personalNote.trim();
        }
      } else if (hasResearch) {
        // Only research info - use it directly (it's already well-formatted)
        finalNarrative = info.trim();
      }

      // Save with direct endpoint
      const response = await authFetch('/api/donors/add-with-group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          groupId: groupId || null,
          notes: finalNarrative  // Server expects 'notes' not 'narrative'
        })
      });

      if (!response.ok) throw new Error('Failed to add person');

      // Refresh donors
      if (onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 300);
      }

      // Navigate to profile
      const result = await response.json();
      if (result.donor) {
        setSelectedPerson({
          id: result.donor.id,
          name: result.donor.name,
          ...result.donor
        });
      }
    } catch (err) {
      console.error('Failed to add person:', err);
      throw err;
    }
  };

  // Handle add person modal research
  const handleAddPersonResearch = async (name, context) => {
    try {
      const response = await authFetch('/api/research-person', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personName: name, existingInfo: context })
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (err) {
      console.error('Research error:', err);
      return null;
    }
  };

  // Open edit group modal
  const handleEditGroup = (group) => {
    setEditingGroup(group);
  };

  // Handle inline member field update (from group detail view)
  const handleUpdateMember = async (memberName, field, value) => {
    try {
      await authPost('/api/donor-update', {
        donorName: memberName,
        field,
        value,
        mode: 'real'
      });

      // Refresh donors to get updated data
      if (onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 300);
      }

      // Find the person's group and refresh lists/tables
      const personData = donors?.[memberName];
      const groupId = personData?.group_id || personData?.groupId;
      if (groupId) {
        setTimeout(() => refreshGroupListsAndTables(groupId), 500);
      }
    } catch (error) {
      console.error('Error updating member:', error);
    }
  };

  // Handle inline table update (name, cell values)
  const handleUpdateTable = async (tableId, updates) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase
        .from('tables')
        .update(updates)
        .eq('id', tableId)
        .eq('user_id', session.user.id);

      // Update local state
      setLocalTables(prev => prev.map(t =>
        t.id === tableId ? { ...t, ...updates } : t
      ));

      // Update active table if it's the one being edited
      if (activeTable?.id === tableId) {
        setActiveTable(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error('Error updating table:', error);
    }
  };

  // Handle inline list update (name)
  const handleUpdateList = async (listId, updates) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase
        .from('lists')
        .update(updates)
        .eq('id', listId)
        .eq('user_id', session.user.id);

      // Update local state
      setLocalLists(prev => prev.map(l =>
        l.id === listId ? { ...l, ...updates } : l
      ));

      // Update active list if it's the one being edited
      if (activeList?.id === listId) {
        setActiveList(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error('Error updating list:', error);
    }
  };


  // Handle navigation from search results
  const handleSearchNavigate = ({ type, id, name, groupId }) => {
    setShowSearch(false);

    switch (type) {
      case 'person':
        setViewIndex(1); // People view
        setSelectedPerson({ name, ...(donors?.[name] || {}) });
        break;
      case 'group':
        setViewIndex(2); // Groups view
        const group = localGroups.find(g => g.id === id);
        if (group) {
          setSelectedGroup(group);
          setShowGroupDetail(true);
        }
        break;
      case 'list':
      case 'table':
        // Navigate to the group containing this list/table
        const parentGroup = localGroups.find(g => g.id === groupId);
        if (parentGroup) {
          setViewIndex(2);
          setSelectedGroup(parentGroup);
          setShowGroupDetail(true);
          // Set active list or table
          if (type === 'list') {
            const list = localLists.find(l => l.id === id);
            if (list) setActiveList(list);
          } else {
            const table = localTables.find(t => t.id === id);
            if (table) setActiveTable(table);
          }
        }
        break;
      case 'feed':
        setViewIndex(0); // Feed view
        // Scroll to specific entry after view renders
        setTimeout(() => {
          feedEntryRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
        break;
      default:
        break;
    }
  };

  // Callback to register feed entry refs
  const handleEntryRef = (entryId, el) => {
    if (el) {
      feedEntryRefs.current[entryId] = el;
    }
  };

  // Render the Feed view (input bar is rendered separately)
  const renderFeedView = () => (
    <MobileFeed
      entries={feedEntries}
      loading={feedLoading}
      donors={donors}
      streamingText={streamingText}
      streamingEntryId={streamingEntryId}
      onNameTap={handleNameTap}
      onGroupSelect={handleGroupSelect}
      onSkipGroupSelect={handleSkipGroupSelect}
      onCreateGroup={handleCreateGroupFromFeed}
      onSaveAsList={handleSaveAsList}
      onViewList={handleViewList}
      onCreateListForGroup={handleCreateListForGroup}
      onEntryRef={handleEntryRef}
      onConfirmAdd={handleConfirmAdd}
      onDeclineAdd={handleDeclineAdd}
      onResearchPerson={handleResearchPerson}
      onSkipResearch={handleSkipResearch}
      onAcceptResearch={handleAcceptResearch}
      onDeclineResearch={handleDeclineResearch}
      onResearchOptionSelect={handleResearchOptionSelect}
      onScrollingUp={setInputCompact}
      onHideNav={setNavHidden}
      onSkipMoreInfo={handleSkipMoreInfo}
    />
  );

  // Render the People view
  const renderPeopleView = () => (
    <MobilePeopleList
      donors={donors}
      onSelectPerson={handleSelectPerson}
    />
  );

  // Render the Groups view
  const renderGroupsView = () => (
    <MobileGroupsList
      groups={localGroups}
      donors={donors}
      savedLists={localLists}
      savedTables={localTables}
      onSelectGroup={handleSelectGroup}
      onSelectPerson={(person) => {
        // Navigate to the person's group and highlight them
        const group = localGroups.find(g => g.id === person.groupId || g.id === person.group_id);
        if (group) {
          handleSelectGroup(group);
          // Set highlighted person after navigation
          setTimeout(() => setHighlightedPerson(person.name), 100);
        } else {
          // If no group, just open profile
          handleSelectPerson(person);
        }
      }}
      onSelectList={(list) => {
        // Find the group for this list and navigate to it
        const group = localGroups.find(g => g.id === list.groupId);
        if (group) {
          handleSelectGroup(group);
          // Set active list after a short delay
          setTimeout(() => setActiveList(list), 100);
        }
      }}
      onSelectTable={(table) => {
        // Find the group for this table and navigate to it
        const group = localGroups.find(g => g.id === table.groupId);
        if (group) {
          handleSelectGroup(group);
          // Set active table after a short delay
          setTimeout(() => setActiveTable(table), 100);
        }
      }}
      onOpenCreateSheet={handleOpenCreateGroupSheet}
    />
  );

  return (
    <div className="mobile-app">
      {/* Top navigation with icons */}
      <TopNav
        currentView={viewIndex}
        onViewChange={(index) => {
          // If in nested view, exit first
          if (showGroupDetail) {
            setShowGroupDetail(false);
            setSelectedGroup(null);
            setActiveList(null);
            setActiveTable(null);
          }
          setViewIndex(index);
          // Show nav when switching views
          setNavHidden(false);
        }}
        onSearchPress={() => setShowSearch(true)}
        onSettingsPress={() => setShowSettings(true)}
        onAddPersonPress={() => setShowAddPersonModal(true)}
        hidden={viewIndex === 0 && navHidden}
        onShowNav={() => setNavHidden(false)}
      />

      {/* Main content area */}
      <div className={`mobile-app-content ${viewIndex === 0 && navHidden ? 'nav-hidden' : ''}`}>
        {!showGroupDetail ? (
          <>
            {/* Render current view based on index */}
            {viewIndex === 0 && renderFeedView()}
            {viewIndex === 1 && renderPeopleView()}
            {viewIndex === 2 && renderGroupsView()}

            {/* Input bar - only show on Feed view */}
            {viewIndex === 0 && (
              <MobileInputBar
                onSubmit={handleSubmitNote}
                isSubmitting={isSubmitting}
                compact={inputCompact}
                onAddPerson={handleOpenAddPersonModal}
              />
            )}
          </>
        ) : (
          /* Group Detail view */
          <MobileGroupDetail
            group={selectedGroup}
            donors={donors}
            onSelectPerson={handleSelectPerson}
            onBack={handleBackFromGroupDetail}
            onEditGroup={handleEditGroup}
            onAddPerson={handleAddPersonToGroup}
            onOpenAddPersonModal={(groupId) => handleOpenAddPersonModal('', '', groupId)}
            onCreatePerson={handleCreatePerson}
            savedLists={localLists}
            onSelectList={handleSelectList}
            activeList={activeList}
            onBackFromList={handleBackFromList}
            onCreateList={handleCreateList}
            savedTables={localTables}
            onSelectTable={handleSelectTable}
            activeTable={activeTable}
            onBackFromTable={handleBackFromTable}
            onCreateTable={handleCreateTable}
            onAddTableColumn={handleAddTableColumn}
            onUpdateGroup={handleUpdateGroup}
            onUpdateMember={handleUpdateMember}
            onUpdateTable={handleUpdateTable}
            onUpdateList={handleUpdateList}
            highlightedPerson={highlightedPerson}
            onClearHighlight={() => setHighlightedPerson(null)}
          />
        )}
      </div>

      {/* Profile overlay */}
      {selectedPerson && (
        <MobileProfile
          isOpen={!!selectedPerson}
          onClose={handleCloseProfile}
          donorName={selectedPerson.name}
          donorData={selectedPerson}
          onSubmitNote={handleAddNoteFromProfile}
          onAskQuestion={(question, personName) => {
            // Close profile, switch to Feed, and submit the question
            handleCloseProfile();
            setViewIndex(0);
            // Small delay to let profile close, then submit
            setTimeout(() => {
              handleSubmitNote(`${question} (about ${personName})`);
            }, 300);
          }}
          onNavigateToPerson={handleNavigateToPerson}
          narrativeUpdate={narrativeUpdate?.personName === selectedPerson.name ? narrativeUpdate : null}
        />
      )}

      {/* Settings overlay */}
      <MobileSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        user={user}
        subscription={subscription}
        onSignOut={onSignOut}
        userContext={userContext}
        onSaveUserContext={handleSaveUserContext}
      />

      {/* Edit group overlay */}
      <MobileEditGroup
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        group={editingGroup}
        onUpdateGroup={handleUpdateGroup}
      />

      {/* Group picker for saving list */}
      {showGroupPicker && (
        <div className="group-picker-overlay" onClick={() => setShowGroupPicker(false)}>
          <div className="group-picker-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="group-picker-header">
              <div className="group-picker-title">Save to which group?</div>
              <button
                className="group-picker-close"
                onClick={() => setShowGroupPicker(false)}
              >
                ×
              </button>
            </div>
            <div className="group-picker-list">
              {localGroups.map((group) => (
                <button
                  key={group.id}
                  className="group-picker-item"
                  onClick={() => handleSaveListToGroup(group)}
                  disabled={savingList}
                >
                  <span
                    className="group-picker-dot"
                    style={{ background: group.color || '#a78bfa' }}
                  />
                  <span className="group-picker-name">{group.name}</span>
                  {savingList && <span className="group-picker-loading">...</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search overlay */}
      <MobileSearch
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        donors={donors}
        groups={localGroups}
        savedLists={localLists}
        savedTables={localTables}
        feedEntries={feedEntries}
        onNavigate={handleSearchNavigate}
      />

      {/* Create group sheet (rendered outside SwipeContainer so position:fixed works) */}
      {showCreateGroupSheet && (
        <div className="groups-create-overlay" onClick={() => { setShowCreateGroupSheet(false); setNewGroupName(''); setNewGroupContext(''); setPendingPersonForGroup(null); }}>
          <div className="groups-create-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="groups-create-handle" />
            <h2 className="groups-create-title">Create a Group</h2>
            <div className="groups-create-input-wrapper">
              <input
                type="text"
                className="groups-create-input"
                placeholder="Group name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setShowCreateGroupSheet(false); setNewGroupName(''); setNewGroupContext(''); setPendingPersonForGroup(null); }
                }}
              />
            </div>
            <div className="groups-create-input-wrapper">
              <input
                type="text"
                className="groups-create-input groups-create-context"
                placeholder="Context (e.g., Series A investors, Board planning)"
                value={newGroupContext}
                onChange={(e) => setNewGroupContext(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitCreateGroupSheet();
                  if (e.key === 'Escape') { setShowCreateGroupSheet(false); setNewGroupName(''); setNewGroupContext(''); setPendingPersonForGroup(null); }
                }}
              />
            </div>
            <button
              className="groups-create-submit"
              onClick={handleSubmitCreateGroupSheet}
              disabled={!newGroupName.trim() || isCreatingGroup}
            >
              {isCreatingGroup ? (
                <span className="groups-create-spinner" />
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create Group
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Add Person Modal */}
      <MobileAddPersonModal
        isOpen={showAddPersonModal}
        onClose={() => {
          setShowAddPersonModal(false);
          setAddPersonPrefill({ name: '', note: '', groupId: null });
        }}
        groups={localGroups}
        onSubmit={handleAddPersonSubmit}
        onResearch={handleAddPersonResearch}
        onCreateGroup={handleCreateGroup}
        prefillName={addPersonPrefill.name}
        prefillNote={addPersonPrefill.note}
        prefillGroupId={addPersonPrefill.groupId}
      />
    </div>
  );
}

export default MobileApp;
